// DroppedChangeToast — app-level banner that surfaces permanently
// dropped offline changes to the field user.
//
// When the PowerSync connector permanently DROPS a queued CRUD entry that
// can never sync — its server-side FK parent was hard-deleted (404
// not_found), or a client UUID collided with an unrelated row (409
// already_exists) — the change lived ONLY on this device. The connector
// records the payload to Sentry and acks the entry so the upload queue
// drains, but without this banner the loss is invisible to the surveyor:
// a tap they made simply vanishes. Asset counts are the contractual
// baseline (AGENTS.md "Business criticality"), so a silent drop is
// unacceptable — the surveyor must be told to re-capture.
//
// Subscribes to the upload-events DROP channel and shows a dismissible
// banner counting how many DISTINCT changes were lost since it was last
// dismissed. A `delete_reverted` drop (a room delete that couldn't apply
// because the building was completed elsewhere) is surfaced on a SEPARATE
// "restored" line — NOT counted as lost data, since the room is intact and
// re-shown by use-delete-room.ts; the surveyor is just told it didn't take.
// The count is keyed by the dropped row (table:id), NOT by
// raw drop events: the connector re-dispatches the whole CRUD batch on
// every upload-retry cycle (~10s) whenever an un-syncable entry can't
// drain, so the SAME drop fires over and over. Counting raw events would
// make "Couldn't save N changes" climb without bound and re-raise the
// banner the instant it was dismissed. Deduping per lost asset means the
// banner shows once per asset; dismissing acknowledges the assets it was
// showing (later re-emits for those are ignored) while a genuinely new
// lost asset still re-surfaces it. There is deliberately NO auto-dismiss:
// data-loss must be explicitly acknowledged so the surveyor knows to
// re-check and re-enter the missing counts. Modelled on AuthExpiredToast
// (role="alert", fixed bottom banner) but driven by the module-level
// drop channel rather than a CaptureClient instance, so it takes no props
// and is mounted once app-wide — a sibling of the router inside
// AuthGatedPowerSyncProvider (see main.tsx), NOT under /capture. The
// connector drains the upload queue app-wide, so a permanent drop can
// fire on any route (e.g. /admin) or mid-redirect; mounting alongside the
// upload provider keeps the subscriber alive whenever uploads run (the
// channel does not buffer).

import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import { AlertTriangle, X } from 'lucide-react'

import type {
  DroppedEvent,
  QuarantinedEvent,
} from '@/hooks/powersync/upload-events'
import {
  subscribeToDrops,
  subscribeToQuarantines,
} from '@/hooks/powersync/upload-events'

export function DroppedChangeToast(): ReactElement | null {
  // Distinct dropped-row keys currently shown, split by kind, plus the keys
  // the surveyor has already dismissed. Refs (not state) because they're
  // mutated from the subscriber and only the derived counts trigger a
  // re-render. They persist for the component's lifetime (mounted once
  // app-wide) — exactly the dedup window we want.
  //   lost     — not_found / id_collision: contractual data-loss → "re-enter".
  //   reverted — delete_reverted: a room delete that couldn't apply because the
  //              building was completed elsewhere. NOT data-loss — the room is
  //              restored (use-delete-room.ts re-shows it); we only tell the
  //              surveyor it didn't take, on a SEPARATE line with its own count.
  //   held     — quarantined: a count that couldn't sync (its server FK parent
  //              was deleted) but was PRESERVED on-device (orphaned_capture_items)
  //              instead of dropped. NOT data-loss — the opposite copy of `lost`:
  //              "held safely, recoverable", so the surveyor isn't told to
  //              re-enter a count that is already saved. Without this line the
  //              connector's drop→quarantine switch (audit-1 #3) would leave the
  //              surveyor with no signal at all that a count needs recovery.
  const shownLostRef = useRef<Set<string>>(new Set())
  const shownRevertedRef = useRef<Set<string>>(new Set())
  const shownHeldRef = useRef<Set<string>>(new Set())
  const dismissedKeysRef = useRef<Set<string>>(new Set())
  const [lostCount, setLostCount] = useState(0)
  const [revertedCount, setRevertedCount] = useState(0)
  const [heldCount, setHeldCount] = useState(0)

  useEffect(() => {
    // Module-level pub/sub. Dedup per dropped row so a drop the connector
    // re-emits on every retry cycle is counted ONCE. Key by table+id (id
    // alone is a UUID, but the composite is the correct identity).
    // `superseded` is deliberately EXCLUDED: a room-alias edit (FFR2) that
    // lost a last-write-wins rename race is cosmetic, not contractual
    // data-loss, and the sync stream delivers the winner's name.
    const unsubscribeDrops = subscribeToDrops((event: DroppedEvent) => {
      if (event.reason === 'superseded') return
      const key = `${event.table}:${event.id}`
      // Already acknowledged — ignore the repeat (either kind).
      if (dismissedKeysRef.current.has(key)) return
      if (event.reason === 'delete_reverted') {
        if (shownRevertedRef.current.has(key)) return
        shownRevertedRef.current.add(key)
        setRevertedCount(shownRevertedRef.current.size)
        return
      }
      // not_found / id_collision — both mean "this change is lost — re-enter".
      if (shownLostRef.current.has(key)) return
      shownLostRef.current.add(key)
      setLostCount(shownLostRef.current.size)
    })
    // Quarantine channel — a count PRESERVED on-device (not lost). Same dedup +
    // dismiss model as drops; surfaced on its own "held safely" line so the
    // surveyor is reassured rather than told to re-enter a saved count. The
    // connector emits `quarantined` (never `dropped`) for a capture_items
    // not_found, so without this subscription the count would vanish from view
    // with no signal at all. (audit-1 #3)
    const unsubscribeQuarantines = subscribeToQuarantines(
      (event: QuarantinedEvent) => {
        const key = `${event.table}:${event.id}`
        if (dismissedKeysRef.current.has(key)) return
        if (shownHeldRef.current.has(key)) return
        shownHeldRef.current.add(key)
        setHeldCount(shownHeldRef.current.size)
      },
    )
    return () => {
      unsubscribeDrops()
      unsubscribeQuarantines()
    }
  }, [])

  if (lostCount === 0 && revertedCount === 0 && heldCount === 0) return null

  const handleDismiss = (): void => {
    // Acknowledge every key currently shown (all kinds) so the connector's
    // retry re-emits don't immediately re-raise the banner. A genuinely NEW
    // key still surfaces again.
    for (const key of shownLostRef.current) dismissedKeysRef.current.add(key)
    for (const key of shownRevertedRef.current) {
      dismissedKeysRef.current.add(key)
    }
    for (const key of shownHeldRef.current) dismissedKeysRef.current.add(key)
    shownLostRef.current.clear()
    shownRevertedRef.current.clear()
    shownHeldRef.current.clear()
    setLostCount(0)
    setRevertedCount(0)
    setHeldCount(0)
  }

  const lostMessage =
    lostCount > 0
      ? `Couldn't save ${lostCount} ${lostCount === 1 ? 'change' : 'changes'}. ` +
        `A room or item was removed on the server before your device synced — ` +
        `please re-check and re-enter anything missing.`
      : null
  const revertedMessage =
    revertedCount > 0
      ? `${revertedCount} room ${revertedCount === 1 ? 'deletion' : 'deletions'} ` +
        `couldn't be applied because the building was completed elsewhere — ` +
        `the ${revertedCount === 1 ? 'room has' : 'rooms have'} been restored.`
      : null
  const heldMessage =
    heldCount > 0
      ? `${heldCount} ${heldCount === 1 ? 'count is' : 'counts are'} held safely ` +
        `on this device — ${heldCount === 1 ? 'it' : 'they'} couldn't sync because ` +
        `a room or item was changed on the server, but nothing was lost.`
      : null
  // Label the dismiss for the most urgent kind present (data-loss > restored >
  // held-safely).
  const dismissLabel =
    lostCount > 0
      ? 'Dismiss lost changes notice'
      : revertedCount > 0
        ? 'Dismiss restored rooms notice'
        : 'Dismiss held counts notice'

  return (
    <div
      role="alert"
      className="fixed bottom-4 left-1/2 z-50 flex w-[min(calc(100vw-2rem),32rem)] -translate-x-1/2 items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-md pb-safe"
    >
      <AlertTriangle
        className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
        aria-hidden="true"
      />
      <div className="flex flex-1 flex-col gap-1">
        {lostMessage !== null && <p>{lostMessage}</p>}
        {revertedMessage !== null && <p>{revertedMessage}</p>}
        {heldMessage !== null && <p>{heldMessage}</p>}
      </div>
      <button
        type="button"
        aria-label={dismissLabel}
        className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
        onClick={handleDismiss}
      >
        <X className="h-4 w-4 text-amber-700" aria-hidden="true" />
      </button>
    </div>
  )
}
